/**
 * PlatformLayout — sidebar terpisah untuk Platform Owner Dashboard.
 * TIDAK memakai AdminLayout supaya branding & navigation berbeda.
 * Identitas platform (nama, logo, warna) ditarik dari PlatformBrandingContext
 * (source: GET /api/platform).
 */
import React from "react";
import { Link, useLocation, useNavigate, Navigate } from "react-router-dom";
import {
  LayoutDashboard, Building2, Globe, Layers, Rocket, Activity, CreditCard, KeyRound, Settings, LogOut, Menu, X, Flag, Package, Sliders, HardDrive, Server, Clock3, Link as LinkIcon, Zap, ClipboardCheck, Users, UserRound, ChevronDown,
} from "lucide-react";
import { usePlatformAuth } from "@/context/PlatformAuthContext";
import { usePlatformBranding } from "@/context/PlatformBrandingContext";
import { hasPermission, PERMISSION } from "@/constants/platformRbac";
import { imageUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PLATFORM_NAV_GROUPS } from "@/constants/platformNavGroups";
import { useNavGroups } from "@/hooks/useNavGroups";

const NAV = [
  { to: "/platform", icon: LayoutDashboard, label: "Dashboard", exact: true },
  { to: "/platform/tenants", icon: Building2, label: "Tenant", perm: PERMISSION.TENANTS_READ },
  { to: "/platform/team", icon: Users, label: "Team", perm: PERMISSION.USERS_READ },
  { to: "/platform/features", icon: Flag, label: "Feature Flags", perm: PERMISSION.TENANTS_READ },
  { to: "/platform/templates", icon: Layers, label: "Templates", perm: PERMISSION.TEMPLATES_READ },
  { to: "/platform/subscriptions", icon: Package, label: "Subscriptions", perm: PERMISSION.SUBSCRIPTIONS_READ },
  { to: "/platform/deployments", icon: Rocket, label: "Deployment", perm: PERMISSION.DEPLOYMENTS_READ },
  { to: "/platform/provisioning", icon: HardDrive, label: "Provisioning", perm: PERMISSION.DEPLOYMENTS_READ },
  { to: "/platform/infrastructure", icon: Server, label: "Infrastructure", perm: PERMISSION.DEPLOYMENTS_READ },
  { to: "/platform/configurations", icon: Sliders, label: "Configuration", perm: PERMISSION.DEPLOYMENTS_READ },
  { to: "/platform/websites", icon: Globe, label: "Website", perm: PERMISSION.TENANTS_READ },
  { to: "/platform/domains", icon: LinkIcon, label: "Domain", perm: PERMISSION.DEPLOYMENTS_READ },
  { to: "/platform/auto-bind", icon: Zap, label: "Auto Bind", perm: PERMISSION.DEPLOYMENTS_WRITE },
  { to: "/platform/go-live", icon: ClipboardCheck, label: "Go Live", perm: PERMISSION.DEPLOYMENTS_READ },
  { to: "/platform/launch", icon: Rocket, label: "Launch Center", perm: PERMISSION.DEPLOYMENTS_WRITE },
  { to: "/platform/template-registry", icon: Layers, label: "Template Registry", perm: PERMISSION.TEMPLATES_READ },
  { to: "/platform/monitoring", icon: Activity, label: "Monitoring", perm: PERMISSION.LOGS_READ },
  { to: "/platform/billing", icon: CreditCard, label: "Billing", perm: PERMISSION.SUBSCRIPTIONS_READ },
  { to: "/platform/licenses", icon: KeyRound, label: "License", perm: PERMISSION.SUBSCRIPTIONS_READ },
  { to: "/platform/scheduler", icon: Clock3, label: "Scheduler", perm: PERMISSION.SETTINGS_READ },
  { to: "/platform/settings", icon: Settings, label: "Branding", perm: PERMISSION.SETTINGS_READ },
  { to: "/platform/settings/landing-page", icon: Globe, label: "Landing Page", perm: PERMISSION.SETTINGS_READ },
  { to: "/platform/account", icon: UserRound, label: "Akun Saya" },
];

/* Identitas AKUN (users.name/email) — bukan branding Dynicty maupun nama toko tenant. */
export function SidebarIdentity({ user }) {
  const name = (user?.name || "").trim();
  return (
    <Link to="/platform/account" className="block min-w-0 group" data-testid="platform-sidebar-identity">
      <div className="text-xs text-white font-bold truncate group-hover:underline" data-testid="platform-sidebar-identity-name">{name || user?.email}</div>
      {name ? <div className="text-[11px] text-white/50 truncate" data-testid="platform-sidebar-identity-email">{user?.email}</div> : null}
    </Link>
  );
}

export default function PlatformLayout({ children, title }) {
  const { user, loading, logout } = usePlatformAuth();
  const { platform } = usePlatformBranding();
  const nav = useNavigate();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  // Aktif = prefix match terpanjang (Branding /platform/settings tidak ikut aktif saat di Landing Page).
  const visibleNav = React.useMemo(() => NAV.filter((item) => !item.perm || hasPermission(user, item.perm)), [user]);
  const isActive = React.useCallback((item) => {
    if (item.exact) return loc.pathname === item.to;
    if (!loc.pathname.startsWith(item.to)) return false;
    return !visibleNav.some((o) => o !== item && !o.exact && o.to.length > item.to.length && loc.pathname.startsWith(o.to));
  }, [loc.pathname, visibleNav]);
  const { entries: navEntries, isOpen: groupOpen, toggle: toggleGroup } = useNavGroups(visibleNav, PLATFORM_NAV_GROUPS, isActive);

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center text-white" style={{ background: "radial-gradient(1200px 600px at 50% 0%, #0D1628, #08111F 70%)" }}>
        <div className="flex items-center gap-3">
          <img src="/brand/dynicty-mark.svg" alt="Dynicty" className="w-10 h-10 animate-pulse" />
          <span className="font-display font-black tracking-wide dynicty-gradient-text text-lg">DYNICTY</span>
        </div>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/platform/login" replace />;
  }
  // must_change_password: kunci ke Akun Saya → Security (backend juga menolak endpoint lain dengan 403).
  if (user.must_change_password && !loc.pathname.startsWith("/platform/account")) {
    return <Navigate to="/platform/account?tab=security" replace />;
  }

  const shortName = (platform.platform_name || "Dynicty").trim();

  const renderNavItem = (item) => {
    const active = isActive(item);
    const Icon = item.icon;
    return (
      <Link
        key={item.to}
        to={item.coming ? "#" : item.to}
        onClick={() => setMobileOpen(false)}
        aria-disabled={item.coming ? true : undefined}
        aria-current={active ? "page" : undefined}
        className={`relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all ${
          active ? "text-white shadow-lg" : "text-white/60 hover:text-white hover:bg-white/5"
        } ${item.coming ? "opacity-50 cursor-not-allowed" : ""}`}
        style={active ? { background: "var(--dynicty-gradient)", boxShadow: "0 8px 20px rgba(0, 212, 255, .15), 0 4px 12px rgba(138, 43, 226, .15)" } : undefined}
        data-testid={`platform-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
      >
        <Icon size={16} />
        <span>{item.label}</span>
        {item.coming && <span className="ml-auto text-[9px] font-bold px-1.5 py-0.5 rounded bg-white/10 text-white/60">SOON</span>}
      </Link>
    );
  };

  return (
    <div className="platform-scope min-h-screen flex" style={{ backgroundColor: "var(--dynicty-bg, #08111F)" }} data-testid="platform-shell">
      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 flex items-center gap-3 px-4 h-14 border-b" style={{ backgroundColor: "var(--dynicty-sidebar, #0D1628)", borderColor: "var(--dynicty-border, #26334F)" }}>
        <button onClick={() => setMobileOpen((v) => !v)} className="text-white p-1" data-testid="platform-mobile-menu-toggle">
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
        <img src="/brand/dynicty-mark.svg" alt="Dynicty" className="w-7 h-7" />
        <div className="font-display font-black tracking-tight dynicty-gradient-text" data-testid="platform-mobile-brand">{shortName}</div>
        <div className="ml-auto text-xs text-white/60">v{platform.platform_version}</div>
      </div>

      {/* Sidebar */}
      <aside className={`${mobileOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0 fixed md:sticky top-0 md:top-auto z-30 h-screen w-64 shrink-0 border-r transition-transform flex flex-col`} style={{ backgroundColor: "var(--dynicty-sidebar, #0D1628)", borderColor: "var(--dynicty-border, #26334F)" }} data-testid="platform-sidebar">
        <div className="px-5 py-5 border-b" style={{ borderColor: "var(--dynicty-border, #26334F)" }}>
          <div className="flex items-center gap-2.5">
            {platform.platform_logo ? (
              <img src={imageUrl(platform.platform_logo)} alt={shortName} className="w-9 h-9 rounded-xl object-cover" onError={(e) => { e.target.src = "/brand/dynicty-mark.svg"; }} data-testid="platform-sidebar-logo" />
            ) : (
              <img src="/brand/dynicty-mark.svg" alt={shortName} className="w-9 h-9" data-testid="platform-sidebar-logo" />
            )}
            <div className="min-w-0">
              <div className="font-display font-black text-base leading-tight truncate dynicty-gradient-text" data-testid="platform-sidebar-name">{shortName}</div>
              <div className="text-[9px] font-bold uppercase tracking-[0.18em] mt-0.5 truncate" style={{ color: "var(--dynicty-text-secondary, #B5C3E0)" }}>{platform.platform_tagline || "BUILD • GROW • EMPOWER"}</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-1 overflow-y-auto" data-testid="platform-sidebar-nav">
          {navEntries.map((entry) => {
            if (entry.type === "item") return renderNavItem(entry.item);
            const GIcon = entry.icon;
            const open = groupOpen(entry.key);
            const groupActive = entry.items.some(isActive);
            return (
              <div key={entry.key} className="pt-1" data-testid={`platform-nav-group-${entry.key}`}>
                <button
                  type="button"
                  onClick={() => toggleGroup(entry.key)}
                  aria-expanded={open}
                  aria-controls={`platform-nav-group-${entry.key}-items`}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-[11px] font-bold uppercase tracking-[0.14em] transition-colors ${
                    groupActive && !open ? "text-white bg-white/5" : "text-white/45 hover:text-white hover:bg-white/5"}`}
                  data-testid={`platform-nav-group-${entry.key}-toggle`}
                >
                  <GIcon size={15} />
                  <span className="flex-1 text-left">{entry.label}</span>
                  <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
                </button>
                {open ? <div id={`platform-nav-group-${entry.key}-items`} className="mt-0.5 space-y-0.5">{entry.items.map(renderNavItem)}</div> : null}
              </div>
            );
          })}
        </nav>

        <div className="p-3 border-t" style={{ borderColor: "var(--dynicty-border, #26334F)" }}>
          <div className="px-3 py-2 mb-2">
            <div className="text-[11px]" style={{ color: "var(--dynicty-text-secondary, #B5C3E0)" }}>Signed in as</div>
            <SidebarIdentity user={user} />
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-white/70 hover:text-white hover:bg-white/5"
            onClick={async () => { await logout(); nav("/platform/login"); }}
            data-testid="platform-logout-btn"
          >
            <LogOut size={16} className="mr-2" /> Logout
          </Button>
          <div className="px-3 text-[10px] text-white/30 mt-2 truncate" data-testid="platform-sidebar-copyright">
            {platform.copyright_text || "© Dynicty"} · v{platform.platform_version}
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 md:ml-0 min-w-0 pt-14 md:pt-0 text-white">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 md:py-8">
          {title ? (
            <div className="mb-6">
              <div className="text-[11px] uppercase tracking-widest font-bold" style={{ color: "var(--dynicty-text-secondary, #B5C3E0)" }} data-testid="platform-page-brand">{shortName}</div>
              <h1 className="font-display text-3xl md:text-4xl font-black mt-1">{title}</h1>
            </div>
          ) : null}
          {children}
        </div>
      </main>
    </div>
  );
}
